<template>
  <v-dialog v-if="datapointExists" v-model="show" max-width="1200px">
    <v-card color="white">
      <div class="d-flex flex-column pa-4">
        <div class="d-flex gap align-center">
          <span class="text-h6 ml-2">{{ $t(`enums.type.${dataPoint.type}`) }} <span class="text-body-1 color-text-secondary">({{ $t(`enums.layers.${dataPoint.layer}`) }})</span></span> 
          <v-spacer />
          <div v-if="dateValues.length" class="text-body-1 color-text-secondary d-flex flex-row flex-wrap gap-2">
            <span class="mr-1">{{ $t(`enums.headers.${dateHeaderKey}`) }}:</span>
            <template v-for="(dateVal, idx) in dateValues">
              <span :key="`date-${idx}`">{{ dateVal }}</span>
              <span v-if="idx < dateValues.length - 1" :key="`sep-${idx}`">, </span>
            </template>
          </div>
          <v-btn text @click="show = false"><strong>X</strong></v-btn> 
        </div>
        <div class="datapoint-image">
          <v-img
            v-if="hasImage"
            :class="isLoading ? 'blurry' : ''"
            :aspect-ratio="16 / 9"
            :height="height"
            :src="imageSrc"
            class="grey darken-4 mt-4"
            contain
          >
            <v-btn
              v-if="!isLoaded && !isLoading"
              outlined
              class="right"
              @click="open(dataPoint)"
            >
              <v-icon>$fas fa-expand-alt</v-icon>
            </v-btn>
          </v-img>
          <v-progress-linear
            v-if="isLoading"
            color="blue-grey"
            indeterminate
            height="25"
          >
            <strong> {{ $t('sviews.global.please-wait') }}</strong>
          </v-progress-linear>
          <v-slider
            v-if="isLoaded"
            v-model="height"
            :label="$t('views.image-upload.size')"
            :thumb-color="'#e2632c'"
            class="align-self-stretch mt-7"
            min="350"
            max="1350"
            step="1"
            thumb-label="always"
          />
        </div>
        <v-simple-table class="mt-4">
          <thead>
            <tr>
              <th
                v-for="(_, key) in specificData"
                :key="'key-' + key"
              >
                {{ $t(`enums.headers.${key}`) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <template v-for="(cellValue, key) in specificData">
                <td
                  v-if="
                    key === 'compaction' || key === 'requiredCompaction'
                  "
                  :key="'value-' + key"
                >
                  {{ $t('core.unit.percent', { n: cellValue.$numberDecimal }) }}
                </td>
                <td
                  v-else-if="typeof cellValue === 'object'"
                  :key="'value-' + key"
                >
                  <span
                    v-for="(v, k) in cellValue"
                    :key="k"
                  >
                    {{ v }}
                  </span>
                </td>
                <td
                  v-else-if="key === 'side'"
                  :key="'value-' + key"
                >
                  {{ $t('enums.side.' + cellValue) }}
                </td>
                <td
                  v-else
                  :key="'value-' + key"
                >
                  {{ cellValue }}
                </td>
              </template>
            </tr>
          </tbody>
        </v-simple-table>
        <v-simple-table class="mt-4">
          <thead>
            <tr>
              <th>{{ $t(`enums.headers.data`) }}</th>
              <th>{{ $t(`enums.headers.type`) }}</th>
              <th>{{ $t(`enums.headers.coordinates`) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {{ dataPoint.data || '-' }}
              </td>
              <td v-if="dataPoint.location">
                {{ dataPoint.location.type || '-' }}
              </td>
              <td v-if="dataPoint.location">
                {{ dataPoint.location.coordinates[1] || '-' }},
                {{ dataPoint.location.coordinates[0] || '-' }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div class="pa-6">
        <v-expansion-panels elevation="0">
          <v-expansion-panel elevation="0">
            <v-expansion-panel-header>JSON</v-expansion-panel-header>
            <v-expansion-panel-content>
              <json-viewer :value="dataPoint" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
import JsonViewer from 'vue-json-viewer'
import axios from 'axios'
import _ from 'lodash'
import purgeObject from '../../../utils/purgeObject'

export default {
  name: 'DataPointPopUp',
  components: { JsonViewer },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    popUPtitle: {
      type: String,
      default: '',
    },
    value: {
      type: Boolean,
      default: false,
    },
    dataPoint: {
      type: Object,
      required: true,
    },
    dataFilterOut: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      imageSrc: '',
      isLoading: false,
      isLoaded: false,
      model: false,
      height: 350,
      dateKeys: ['date'],
      toRemoveKeys: [
        '_id',
        'user',
        'externalIdentifier',
        'location',
        'source',
        'createdAt',
        'date',
        'updatedAt',
        'type',
        'typeUI',
        'data',
        'layer',
        'mixDesignCodes',
        'aggregateSource',
      ],
    }
  },
  computed: {
    datapointExists() {
      return !_.isEmpty(this.dataPoint)
    },
    datesData() {
      let result = {}
      for (const key of Object.keys(this.dataPoint).filter((x) =>
        this.dateKeys.includes(x),
      )) {
        if (
          this.dataFilterOut.indexOf(key) === -1 &&
          this.dataPoint[key] !== null
        ) {
          result[key] = this.$helpers.date.formatDateAsIs(this.dataPoint[key])
        }
      }
      return result
    },
    dateHeaderKey() {
      return this.dateKeys && this.dateKeys.length ? this.dateKeys[0] : 'date'
    },
    dateValues() {
      return Object.values(this.datesData)
    },
    specificData() {
      let result = {}

      const filteredDatapointKeys = Object.keys(this.dataPoint).filter(
        (key) => !this.toRemoveKeys.includes(key),
      )
      for (const key of filteredDatapointKeys) {
        if (
          this.dataFilterOut.indexOf(key) === -1 &&
          this.dataPoint[key] !== null
        ) {
          result[key] = this.dataPoint[key]
        }
      }
      result = purgeObject(result)
      return result
    },
    source: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    hasImage() {
      return this.dataPoint.type === 'images'
    },
  },
  watch: {
    show() {
      this.imageSrc = ''
      this.height = 350
      this.isLoaded = false

      if (this.dataPoint.type === 'images' && this.show) {
        this.$http
          .request({
            url: `/datapoints/${this.dataPoint._id}/thumbnail`,
            responseType: 'blob',
          })
          .then((blob) => {
            this.imageSrc = URL.createObjectURL(blob.data)
          })
          .catch((error) => console.error(error))
      }
    },
  },
  methods: {
    async open(value) {
      this.isLoading = true

      await axios
        .get(`/datapoints/${value._id}/image`, {
          responseType: 'blob',
        })
        .then((result) => {
          this.height = 700
          this.imageSrc = URL.createObjectURL(result.data)
          this.isLoading = false
          this.isLoaded = true
        })
    },
  },
}
</script>
<style scoped lang="scss">
img {
  display: block;
  margin: auto;
}

.v-card > .v-card__progress + :not(.v-btn):not(.v-chip),
.v-card > :first-child:not(.v-btn):not(.v-chip) {
  border-top-left-radius: unset;
  border-top-right-radius: unset;
}

::v-deep .v-expansion-panel::before {
  box-shadow: none !important;
}

::v-deep .v-expansion-panel-header {
  padding: 0;
  min-height: 32px;
  .v-expansion-panel-header__icon {
    margin: 0 0 0 16px;
    .v-icon__component {
      height: 18px;
      width: 18px;
    }
  }
}
::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
::v-deep .jv-container .jv-code {
  padding: 0;
}

</style>
